import React from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Phone, Clock, CheckCircle, Shield, Star, ArrowRight, MapPin } from "lucide-react";
import { Header, Footer, StickyCTA, QuoteForm } from "../components/Layout";
import seoData from "../data/seoVilles.json";

// Services disponibles
const SERVICES = {
  deratisation: {
    nom: "Dératisation",
    slug: "deratisation",
    icon: "🐀",
    description: "Élimination complète des rats et souris",
    problemes: ["Rats dans les caves", "Souris dans les murs", "Rongeurs dans le jardin", "Bruits de grattement la nuit"],
    methodes: ["Pose d'appâts sécurisés", "Pièges professionnels", "Obturation des accès", "Suivi et prévention"],
    prix: "À partir de 120€",
    faq: (ville) => [
      { q: `Combien coûte une dératisation à ${ville.nom} ?`, a: `Une dératisation à ${ville.nom} (${ville.cp}) coûte à partir de 120€ pour un logement. Le tarif exact dépend de la surface et du niveau d'infestation. Azur Tech propose un devis gratuit et sans engagement, par téléphone ou sur place.` },
      { q: `En combien de temps intervenez-vous à ${ville.nom} ?`, a: `Nous intervenons à ${ville.nom} le jour même en cas d'urgence, souvent sous 2 heures. Notre équipe est disponible 24h/24 et 7j/7, week-end inclus, dans tout le secteur de ${ville.nom} et ses environs.` },
      { q: `Comment savoir si j'ai des rats ou des souris chez moi à ${ville.nom} ?`, a: `Les signes révélateurs sont : crottes (2 cm pour les rats, 3-8 mm pour les souris), bruits de grattement la nuit, traces de gras le long des murs, câbles ou emballages rongés. En cas de doute, appelez-nous : le diagnostic est gratuit.` },
      { q: `Les appâts utilisés sont-ils dangereux pour mes animaux ?`, a: `Non. Nous utilisons des postes d'appâtage sécurisés et fermés à clé, inaccessibles aux enfants et aux animaux domestiques. Nos techniciens sont certifiés Certibiocide.` },
    ],
  },
  desinsectisation: {
    nom: "Désinsectisation",
    slug: "desinsectisation",
    icon: "🪳",
    description: "Traitement contre tous les insectes nuisibles",
    problemes: ["Cafards et blattes", "Fourmis envahissantes", "Puces et tiques", "Araignées"],
    methodes: ["Traitement gel appât", "Pulvérisation ciblée", "Nébulisation", "Traitement préventif"],
    prix: "À partir de 80€",
    faq: (ville) => [
      { q: `Combien coûte une désinsectisation à ${ville.nom} ?`, a: `Une désinsectisation à ${ville.nom} (${ville.cp}) coûte à partir de 80€ pour un traitement ponctuel. Le prix varie selon la surface, le type d'insecte et le niveau d'infestation. Devis gratuit et sans engagement.` },
      { q: `Combien de temps dure un traitement contre les cafards à ${ville.nom} ?`, a: `Un traitement anti-cafards nécessite généralement 2 à 3 passages espacés de 15 jours. Le premier passage élimine les adultes, les suivants éliminent les jeunes issus des œufs. Résultat garanti : on revient gratuitement si le problème persiste.` },
      { q: `Les produits utilisés sont-ils dangereux ?`, a: `Nous utilisons des produits professionnels homologués, appliqués de manière ciblée par des techniciens certifiés. Ils sont sans danger pour les humains et animaux domestiques après séchage (2 à 4 heures).` },
      { q: `Intervenez-vous en urgence à ${ville.nom} ?`, a: `Oui, nous intervenons en urgence 24h/24 et 7j/7 à ${ville.nom} et dans les environs, souvent sous 2 heures pour les cas urgents.` },
    ],
  },
  "punaises-de-lit": {
    nom: "Punaises de lit",
    slug: "punaises-de-lit",
    icon: "🛏️",
    description: "Éradication totale des punaises de lit",
    problemes: ["Piqûres au réveil", "Taches sur les draps", "Insectes dans le matelas", "Démangeaisons nocturnes"],
    methodes: ["Traitement thermique 56°C", "Traitement chimique pro", "Vapeur sèche", "Traitement combiné"],
    prix: "À partir de 150€",
    faq: (ville) => [
      { q: `Combien coûte un traitement punaises de lit à ${ville.nom} ?`, a: `Un traitement punaises de lit à ${ville.nom} (${ville.cp}) coûte à partir de 150€ par pièce en traitement chimique, et 400 à 600€ en traitement thermique. Le thermique est efficace à 100% en un seul passage.` },
      { q: `Le traitement thermique est-il vraiment efficace ?`, a: `Oui, le traitement thermique à 56°C tue toutes les punaises et leurs œufs en un seul passage. C'est la méthode la plus efficace, recommandée pour les infestations importantes.` },
      { q: `Faut-il jeter son matelas ?`, a: `Non, dans la plupart des cas le matelas peut être traité et conservé. Jeter le matelas sans traitement risque de contaminer d'autres zones. Nous vous conseillons sur place.` },
      { q: `En combien de temps intervenez-vous à ${ville.nom} ?`, a: `Nous intervenons rapidement à ${ville.nom}, souvent sous 24 à 48h pour les punaises de lit. Plus vous agissez tôt, plus le traitement est simple et économique.` },
    ],
  },
  "punaise-de-lit": {
    nom: "Punaises de lit",
    slug: "punaises-de-lit",
    icon: "🛏️",
    description: "Éradication totale des punaises de lit",
    problemes: ["Piqûres au réveil", "Taches sur les draps", "Insectes dans le matelas", "Démangeaisons nocturnes"],
    methodes: ["Traitement thermique 56°C", "Traitement chimique pro", "Vapeur sèche", "Traitement combiné"],
    prix: "À partir de 150€",
    faq: (ville) => [
      { q: `Combien coûte un traitement punaises de lit à ${ville.nom} ?`, a: `Un traitement punaises de lit à ${ville.nom} (${ville.cp}) coûte à partir de 150€ par pièce en traitement chimique, et 400 à 600€ en traitement thermique.` },
      { q: `Le traitement thermique est-il vraiment efficace ?`, a: `Oui, le traitement thermique à 56°C tue toutes les punaises et leurs œufs en un seul passage.` },
    ],
  },
  "destruction-nid-guepe-frelon": {
    nom: "Destruction nid de guêpes/frelons",
    slug: "destruction-nid-guepe-frelon",
    icon: "🐝",
    description: "Retrait sécurisé des nids de guêpes et frelons",
    problemes: ["Nid sous le toit", "Guêpes dans les volets", "Frelons dans le jardin", "Nid dans un arbre"],
    methodes: ["Intervention sécurisée", "Équipement professionnel", "Retrait ou destruction", "Urgence 24h/24"],
    prix: "À partir de 80€",
    faq: (ville) => [
      { q: `Combien coûte la destruction d'un nid de guêpes à ${ville.nom} ?`, a: `La destruction d'un nid de guêpes ou de frelons à ${ville.nom} (${ville.cp}) coûte à partir de 80€. Le prix dépend de la hauteur et de l'accessibilité du nid. Devis gratuit par téléphone.` },
      { q: `Intervenez-vous en urgence pour un nid de frelons à ${ville.nom} ?`, a: `Oui, nous intervenons en urgence 24h/24 à ${ville.nom}, notamment en cas de danger immédiat (allergie, présence d'enfants, nid près d'une entrée).` },
      { q: `Peut-on détruire un nid de guêpes soi-même ?`, a: `C'est fortement déconseillé. Les piqûres multiples peuvent provoquer un choc anaphylactique. Nos techniciens sont équipés de combinaisons intégrales et de perches télescopiques pour intervenir en toute sécurité.` },
      { q: `Frelon asiatique ou européen : quelle différence ?`, a: `Le frelon asiatique (pattes jaunes, thorax noir) est plus agressif et son nid doit être signalé. Nous identifions l'espèce et procédons à la destruction dans les règles.` },
    ],
  },
  desinfection: {
    nom: "Désinfection",
    slug: "desinfection",
    icon: "🦠",
    description: "Désinfection professionnelle des locaux",
    problemes: ["Après infestation", "Odeurs persistantes", "Contamination", "Problème d'hygiène"],
    methodes: ["Nébulisation", "Traitement surfaces", "Traitement de l'air", "Ozonation"],
    prix: "Sur devis",
    faq: (ville) => [
      { q: `Quand faut-il faire une désinfection à ${ville.nom} ?`, a: `Une désinfection est recommandée après une infestation de rongeurs (déjections contaminées), après un sinistre (inondation, décès) ou pour traiter des odeurs persistantes. Nous intervenons chez les particuliers et professionnels de ${ville.nom}.` },
      { q: `Combien de temps dure une désinfection ?`, a: `Une désinfection prend généralement 2 à 4 heures selon la surface. Le local est accessible après 4 à 6 heures de séchage et d'aération.` },
      { q: `La désinfection élimine-t-elle les odeurs ?`, a: `Oui, nous utilisons des techniques spécifiques (ozonation, nébulisation) pour neutraliser les odeurs tenaces.` },
    ],
  },
};

const VILLES = Object.fromEntries(seoData.villes.map((v) => [v.slug, v]));

export default function ServiceVillePage() {
  const path = window.location.pathname.replace(/^\//, "").replace(/\/$/, "");

  let serviceKey = null;
  let villeKey = null;
  for (const sKey of Object.keys(SERVICES)) {
    if (path.startsWith(sKey + "-")) {
      serviceKey = sKey;
      villeKey = path.replace(sKey + "-", "");
      break;
    }
  }

  const service = SERVICES[serviceKey];
  const ville = VILLES[villeKey];

  if (!service || !ville) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <div className="container mx-auto px-4 py-20 text-center">
          <h1 className="text-3xl font-bold text-gray-800 mb-4">Page non trouvée</h1>
          <p className="text-gray-600 mb-8">Cette page n'existe pas.</p>
          <Link to="/" className="text-emerald-500 hover:underline">Retour à l'accueil</Link>
        </div>
        <Footer />
      </div>
    );
  }

  const canonicalUrl = `https://azurtech06.fr/${service.slug}-${villeKey}`;
  const title = `${service.nom} ${ville.nom} (${ville.cp}) – Intervention 24h/24 | Azur Tech`;
  const metaDescription = `${service.nom} à ${ville.nom} (${ville.dept}) : intervention rapide 24h/24, ${service.prix.toLowerCase()}, résultat garanti. Expert certifié depuis 2015, +5000 interventions ☎ 07 64 07 95 35`;
  const faqItems = service.faq ? service.faq(ville) : [];
  const est06 = ville.dept === "06";

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "Service",
      name: `${service.nom} ${ville.nom}`,
      serviceType: service.nom,
      description: `${service.description} à ${ville.nom} (${ville.cp}) et ses environs.`,
      url: canonicalUrl,
      provider: {
        "@type": "PestControlService",
        "@id": "https://azurtech06.fr/#organization",
        name: "Azur Tech",
        telephone: "+33764079535",
        url: "https://azurtech06.fr",
      },
      areaServed: { "@type": "City", name: ville.nom, address: { "@type": "PostalAddress", postalCode: ville.cp, addressRegion: ville.region, addressCountry: "FR" } },
      ...(SERVICES[serviceKey].prix !== "Sur devis" && {
        offers: { "@type": "Offer", priceCurrency: "EUR", price: service.prix.replace(/\D/g, ""), description: service.prix },
      }),
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Accueil", item: "https://azurtech06.fr/" },
        { "@type": "ListItem", position: 2, name: service.nom, item: `https://azurtech06.fr/${service.slug}` },
        { "@type": "ListItem", position: 3, name: `${service.nom} ${ville.nom}`, item: canonicalUrl },
      ],
    },
    ...(faqItems.length > 0
      ? [{
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: faqItems.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }]
      : []),
  ];

  const IDF_DEPTS = ["75", "77", "78", "91", "92", "93", "94", "95"];
  const estIDF = IDF_DEPTS.includes(ville.dept);
  const villesProches = seoData.villes
    .filter((v) => v.slug !== villeKey && (estIDF ? IDF_DEPTS.includes(v.dept) : v.dept === ville.dept))
    .slice(0, 15);

  return (
    <div className="min-h-screen bg-white pb-16 md:pb-0">
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={metaDescription} />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:url" content={canonicalUrl} />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>
      <Header />

      {/* Hero */}
      <section className="bg-gradient-to-br from-[#0f172a] to-[#1e3a5f] text-white py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl">
            <div className="text-4xl mb-4">{service.icon}</div>
            <h1 className="text-3xl md:text-4xl font-bold mb-4">
              {service.nom} à {ville.nom} ({ville.cp})
            </h1>
            <p className="text-xl text-gray-300 mb-6">
              {service.description}. Intervention rapide dans tout {ville.nom} et ses environs
              {est06 ? " dans les Alpes-Maritimes" : ""}.
            </p>
            <div className="flex flex-wrap gap-4">
              <a href="tel:0764079535" data-testid="sv-hero-phone-btn" className="inline-flex items-center space-x-2 bg-emerald-500 text-white px-6 py-3 rounded-xl font-bold hover:bg-emerald-600 transition-all">
                <Phone className="w-5 h-5" />
                <span>07 64 07 95 35</span>
              </a>
              <Link to="/#devis" data-testid="sv-hero-devis-btn" className="inline-flex items-center space-x-2 bg-white/10 text-white px-6 py-3 rounded-xl font-bold hover:bg-white/20 transition-all">
                <span>Devis gratuit</span>
                <ArrowRight className="w-5 h-5" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Quartiers desservis */}
      {ville.quartiers && (
        <section className="py-8 bg-emerald-50 border-b border-emerald-100">
          <div className="container mx-auto px-4">
            <div className="flex items-start space-x-3">
              <MapPin className="w-5 h-5 text-emerald-600 mt-1 flex-shrink-0" />
              <p className="text-gray-700">
                <strong>Nous intervenons dans tous les quartiers de {ville.nom} :</strong>{" "}
                {ville.quartiers.join(", ")} et alentours. Déplacement rapide, souvent sous 2 heures en urgence.
              </p>
            </div>
          </div>
        </section>
      )}

      {/* Problèmes traités */}
      <section className="py-12 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-2xl font-bold text-gray-800 mb-8">
            Problèmes traités à {ville.nom}
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {service.problemes.map((probleme, index) => (
              <div key={index} className="bg-white p-4 rounded-xl border border-gray-200 flex items-center space-x-3">
                <CheckCircle className="w-5 h-5 text-emerald-500 flex-shrink-0" />
                <span className="text-gray-700">{probleme}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Nos méthodes */}
      <section className="py-12 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-2xl font-bold text-gray-800 mb-8">
            Notre méthode de {service.nom.toLowerCase()}
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {service.methodes.map((methode, index) => (
              <div key={index} className="text-center">
                <div className="w-12 h-12 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-4">
                  {index + 1}
                </div>
                <p className="text-gray-700 font-medium">{methode}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pourquoi nous */}
      <section className="py-12 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-2xl font-bold text-gray-800 mb-8">
            Pourquoi choisir Azur Tech à {ville.nom} ?
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-white p-6 rounded-xl border border-gray-200">
              <Clock className="w-10 h-10 text-emerald-500 mb-4" />
              <h3 className="font-bold text-gray-800 mb-2">Intervention rapide</h3>
              <p className="text-gray-600">Disponible 24h/24 et 7j/7. Intervention le jour même à {ville.nom}.</p>
            </div>
            <div className="bg-white p-6 rounded-xl border border-gray-200">
              <Shield className="w-10 h-10 text-emerald-500 mb-4" />
              <h3 className="font-bold text-gray-800 mb-2">Résultat garanti</h3>
              <p className="text-gray-600">Si le problème persiste, on revient gratuitement.</p>
            </div>
            <div className="bg-white p-6 rounded-xl border border-gray-200">
              <Star className="w-10 h-10 text-emerald-500 mb-4" />
              <h3 className="font-bold text-gray-800 mb-2">Expert depuis 2015</h3>
              <p className="text-gray-600">+5000 interventions réalisées {est06 ? "dans les Alpes-Maritimes" : `en ${ville.region}`}.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Tarif */}
      <section className="py-12 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-xl mx-auto bg-emerald-50 p-8 rounded-2xl border border-emerald-200 text-center">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">
              Tarif {service.nom.toLowerCase()} à {ville.nom}
            </h2>
            <p className="text-3xl font-bold text-emerald-600 mb-4">{service.prix}</p>
            <p className="text-gray-600 mb-6">Devis gratuit et sans engagement</p>
            <a href="tel:0764079535" data-testid="sv-tarif-phone-btn" className="inline-flex items-center space-x-2 bg-emerald-500 text-white px-8 py-4 rounded-xl font-bold hover:bg-emerald-600 transition-all">
              <Phone className="w-5 h-5" />
              <span>Appelez maintenant</span>
            </a>
          </div>
        </div>
      </section>

      {/* FAQ locale */}
      {faqItems.length > 0 && (
        <section className="py-12 bg-gray-50" data-testid="sv-faq-section">
          <div className="container mx-auto px-4">
            <h2 className="text-2xl font-bold text-gray-800 mb-8">
              Questions fréquentes – {service.nom} à {ville.nom}
            </h2>
            <div className="max-w-3xl space-y-4">
              {faqItems.map((item, index) => (
                <details key={index} className="bg-white rounded-xl border border-gray-200 overflow-hidden group">
                  <summary className="px-6 py-4 cursor-pointer font-medium text-gray-800 hover:bg-gray-100 transition-colors flex justify-between items-center">
                    {item.q}
                    <span className="text-emerald-500 group-open:rotate-180 transition-transform ml-4">▼</span>
                  </summary>
                  <div className="px-6 py-4 text-gray-600 border-t border-gray-200">
                    {item.a}
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Formulaire */}
      <section className="py-12 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-xl mx-auto">
            <h2 className="text-2xl font-bold text-gray-800 mb-6 text-center">
              Demandez un devis gratuit
            </h2>
            <QuoteForm />
          </div>
        </div>
      </section>

      {/* Maillage interne */}
      <section className="py-12 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-xl font-bold text-gray-800 mb-6">
            {service.nom} dans d'autres villes {est06 ? "des Alpes-Maritimes" : estIDF ? "d'Île-de-France" : `(${ville.region})`}
          </h2>
          <div className="flex flex-wrap gap-2 mb-8">
            {villesProches.map((v) => (
              <Link
                key={v.slug}
                to={`/${service.slug}-${v.slug}`}
                className="px-3 py-1 rounded-full text-sm bg-white border border-gray-200 text-gray-600 hover:bg-emerald-50 hover:text-emerald-600 hover:border-emerald-200 transition-colors"
              >
                {service.nom} {v.nom}
              </Link>
            ))}
          </div>

          <h3 className="text-lg font-semibold text-gray-800 mb-4">Autres services à {ville.nom}</h3>
          <div className="flex flex-wrap gap-3">
            {Object.entries(SERVICES).filter(([k]) => k !== serviceKey && k !== "punaise-de-lit").map(([key, s]) => (
              <Link
                key={key}
                to={`/${s.slug}-${villeKey}`}
                className="px-4 py-2 bg-white border border-gray-200 text-gray-700 rounded-lg hover:bg-emerald-50 hover:text-emerald-600 transition-colors"
              >
                {s.nom} {ville.nom}
              </Link>
            ))}
          </div>
        </div>
      </section>

      <Footer />
      <StickyCTA />
    </div>
  );
}
